import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Info, History, Calculator, ChevronLeft, ChevronRight,
  UserPlus, Settings, LogOut,
} from "lucide-react";
import { useAuthMutations } from "../hooks/useAuthMutations";
import { FloatingChatLauncher } from "../chat";

const NAV_ITEMS = [
  { label: "Home", mobileLabel: "Home", path: "/home", icon: Home },
  { label: "About", mobileLabel: "About", path: "/about", icon: Info },
  { label: "Transaction History", mobileLabel: "History", path: "/transactionhistory", icon: History },
  { label: "FX Calculator", mobileLabel: "FX", path: "/fxcalculator", icon: Calculator },
];

// Desktop-only — actions, not destinations. Kept off the mobile bar on purpose;
// they live in ProfileOverlay / a "+" button on mobile instead.
const MANAGE_ITEMS = [
  { label: "Add Recipient", path: "/createrecipient", icon: UserPlus },
  { label: "Profile Settings", path: "/profile/edit", icon: Settings },
];

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const { logout, isLoggingOut } = useAuthMutations();

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* SIDEBAR */}
      <aside
        className={`hidden md:flex flex-col shrink-0 border-r border-gray-100 transition-all duration-300 ease-in-out ${
          collapsed ? "w-20" : "w-64"
        }`}
      >
        <div className={`flex items-center h-20 px-6 ${collapsed ? "justify-center" : "justify-between"}`}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img
                src="/BlupayLogo.png"
                alt="BluPay"
                className="w-8 h-8 object-contain"
              />

              <h1 className="text-2xl font-serif font-semibold tracking-tight">
                BluPay
              </h1>
            </div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* PRIMARY NAV */}
        <nav className="px-4 py-2 space-y-1">
          {NAV_ITEMS.map(({ label, path, icon: Icon }) => {
            const active = isActive(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                title={collapsed ? label : undefined}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-200 ${
                  collapsed ? "justify-center" : ""
                } ${
                  active
                    ? "text-[#0A4730] bg-[#C0F6B7]/30 font-semibold"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                {!collapsed && <span className="text-sm">{label}</span>}
              </button>
            );
          })}
        </nav>

        {/* MANAGE */}
        <div className="mt-4 px-4 text-left">
          {!collapsed && (
            <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400">
              Manage
            </p>
          )}
          <nav className="space-y-1">
            {MANAGE_ITEMS.map(({ label, path, icon: Icon }) => {
              const active = isActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  title={collapsed ? label : undefined}
                  className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-200 ${
                    collapsed ? "justify-center" : ""
                  } ${
                    active
                      ? "text-[#0A4730] bg-[#C0F6B7]/30 font-semibold"
                      : "text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                  {!collapsed && <span className="text-sm">{label}</span>}
                </button>
              );
            })}
          </nav>
        </div>

        {/* FOOTER — logout, pinned to bottom */}
        <div className="border-t border-gray-100 p-4 space-y-1">
          

          <button
            onClick={() =>
              logout(undefined, { onSuccess: () => navigate("/login") })
            }
            disabled={isLoggingOut}
            title={collapsed ? "Log out" : undefined}
            className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-red-400 hover:bg-red-50 transition disabled:opacity-50 ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <LogOut size={18} />
            {!collapsed && (
              <span className="text-sm font-medium">
                {isLoggingOut ? "Signing out…" : "Log out"}
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-y-auto pb-24 md:pb-0 md:p-12">
        <div className="max-w-5xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* BLUPAY CHAT */}
      <FloatingChatLauncher />

      {/* MOBILE BOTTOM NAV — unchanged, 4 icons only */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-md border-t border-gray-100 px-6 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between max-w-md mx-auto">
          {NAV_ITEMS.map(({ label, mobileLabel, path, icon: Icon }) => {
            const active = isActive(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className="flex flex-col items-center gap-1 flex-1"
              >
                <div
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition ${
                    active ? "bg-[#C0F6B7]" : "bg-transparent"
                  }`}
                >
                  <Icon
                    size={19}
                    strokeWidth={active ? 2.4 : 2}
                    className={active ? "text-[#0A4730]" : "text-[#0a2540]/40"}
                  />
                </div>
                <span
                  className={`text-[9px] font-medium tracking-tight ${
                    active ? "text-[#0A4730]" : "text-[#0a2540]/40"
                  }`}
                >
                  {mobileLabel || label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}